import React from 'react';
import {
    selectCurrentProject,
    selectSelectedSocialMedia,
} from '../../../../store/selectors/projectSelectors';
import { useSelector } from 'react-redux';
import IconIstagramLike from '../../../../shared/ui/icons/IconIstagramLike';
import IconIstagramComment from '../../../../shared/ui/icons/IconIstagramComment';
import IconIstagramSave from '../../../../shared/ui/icons/IconIstagramSave';
import IconIstagramShare from '../../../../shared/ui/icons/IconIstagramShare';
import s from './PreviewContainer.module.css';
import IconThreadsShare from '../../../../shared/ui/icons/IconThreadsShare';
import IconLinkedinLike from '../../../../shared/ui/icons/IconLinkedinLike';
import IconVideoPlay from '../../../../shared/ui/icons/IconVideoPlay';
import IconLinkedinGraph from '../../../../shared/ui/icons/IconLinkedinGraph';
import IconIstagramUpload from '../../../../shared/ui/icons/IconIstagramUpload';
import AvatarComponent from '../../../../shared/ui/avatar/AvatarComponent';
import { getShortTextFromHtml } from '../../../../shared/helpers/transformData';

type PreviewContainerProps = {
    avatarUrl: string;
    userName: string;
    userHandle?: string;
    text: string;
    mediaUrl?: string;
    mediaType?: 'image' | 'video';
};

const dimensions = {
    twitter: {
        width: 453,
        height: 453,
    },
    tiktok: {
        width: 453,
        height: 579,
    },
    youtube: {
        width: 453,
        height: 579,
    },
    pinterest: {
        width: 491,
        height: 491,
    },
    linkedin: {
        width: 488,
        height: 510,
    },
    instagram: {
        width: 488,
        height: 510,
    },
    threads: {
        width: 450,
        height: 563,
    },
    facebook: {
        width: 450,
        height: 453,
    },
};
const PreviewContainer: React.FC<PreviewContainerProps> = ({
    avatarUrl,
    userName,
    userHandle,
    text,
    mediaUrl,
    mediaType = 'image',
}) => {
    const currentProject = useSelector(selectCurrentProject);
    const platform =
        useSelector(selectSelectedSocialMedia) || currentProject?.content_type || 'linkedin';

    const getShortText = (text: string) => {
        return getShortTextFromHtml(text);
    };

    const width = dimensions[platform as keyof typeof dimensions].width;
    const height = dimensions[platform as keyof typeof dimensions].height;

    if (platform === 'twitter') {
        return (
            <div className={`${s.container} `}>
                <div
                    className={s.x}
                    style={{
                        width: 400,
                    }}
                >
                    <div className={s.headerWrapper}>
                        <div className={s.header}>
                            <AvatarComponent avatarUrl={avatarUrl} userName={userName} />
                            <div className={s.userInfo}>
                                <span className={s.name}>{userName}</span>
                                {userHandle && <span className={s.handle}>@{userHandle}</span>}
                            </div>
                        </div>
                        <div className={s.moreOptions}>⋯</div>
                    </div>
                    <div className={s.text}>{getShortText(text)}</div>

                    {mediaUrl &&
                        (mediaType === 'image' ? (
                            <img
                                src={mediaUrl}
                                alt="media"
                                className={s.media}
                                // style={{
                                //     width: '100%',
                                //     aspectRatio: `${width || 1} / ${height || 1}`,
                                // }}
                            />
                        ) : (
                            <video
                                src={mediaUrl}
                                controls
                                className={s.media}
                                style={{
                                    width: '100%',
                                    aspectRatio: `${width || 1} / ${height || 1}`,
                                }}
                            />
                        ))}
                    <div className={s.actions}>
                        <button>
                            <IconIstagramComment />
                        </button>
                        <button>
                            <IconThreadsShare />
                        </button>
                        <button>
                            <IconIstagramLike />
                        </button>
                        <button>
                            <IconLinkedinGraph />
                        </button>
                        <div className={s.rightActions}>
                            <button>
                                <IconIstagramSave />
                            </button>
                            <button>
                                <IconIstagramUpload />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (platform === 'tiktok' || platform === 'youtube') {
        return (
            <div className={`${s.container} `}>
                <div
                    className={s.youtube}
                    style={{
                        width: width || 453,
                        height: '100%',
                    }}
                >
                    <div className={s.video_play}>
                        <IconVideoPlay />
                    </div>
                    {mediaType === 'image' ? (
                        <img
                            src={mediaUrl}
                            alt="media"
                            className={s.media}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                    ) : (
                        <video
                            src={mediaUrl}
                            controls
                            className={s.media}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                    )}
                    <div className={s.overlay}></div>
                    <div className={s.overlayContent}>
                        <div className={s.channelRow}>
                            <AvatarComponent avatarUrl={avatarUrl} userName={userName} />
                            <span className={s.name}>{userName}</span>
                            <button className={s.subscribeBtn}>Subscribe</button>
                        </div>
                        <div className={s.caption}>{getShortText(text)}</div>
                    </div>
                </div>
            </div>
        );
    }

    if (platform === 'pinterest') {
        return (
            <div className={`${s.container} `}>
                <div
                    className={s.pinterest}
                    style={{
                        width: 400,
                    }}
                >
                    <div className={s.mediaWrapper}>
                        {mediaUrl &&
                            (mediaType === 'image' ? (
                                <img
                                    src={mediaUrl}
                                    alt="media"
                                    className={s.media}
                                    // style={{
                                    //     width: width || 491,
                                    // }}
                                />
                            ) : (
                                <video
                                    src={mediaUrl}
                                    controls
                                    className={s.media}
                                    style={{
                                        width: width || 491,
                                        height: height || 491,
                                    }}
                                />
                            ))}
                    </div>
                    <div className={s.actions}>
                        <button>
                            <IconIstagramLike />
                        </button>
                        <button>
                            <IconIstagramComment />
                        </button>
                        <button>
                            <IconIstagramUpload />
                        </button>
                        <button className={s.saveBtn}>Save</button>
                    </div>
                    <div className={s.info}>
                        <div className={s.user}>
                            <AvatarComponent avatarUrl={avatarUrl} userName={userName} />
                            <span className={s.name}>{userName}</span>
                        </div>
                        <div className={s.title}>{getShortText(text)}</div>
                    </div>
                </div>
            </div>
        );
    }

    if (platform === 'linkedin') {
        return (
            <div className={`${s.container} `}>
                <div
                    className={s.linkedin}
                    style={{
                        width: 400,
                    }}
                >
                    <div className={s.header}>
                        <AvatarComponent avatarUrl={avatarUrl} userName={userName} />
                        <div className={s.userInfo}>
                            <span className={s.name}>{userName}</span>
                            <span className={s.title}>Position · Company</span>
                        </div>
                    </div>
                    <div className={s.text}>{getShortText(text)}</div>
                    {mediaUrl &&
                        (mediaType === 'image' ? (
                            <img
                                src={mediaUrl}
                                alt="media"
                                className={s.media}
                                // style={{
                                //     width: width || 488,
                                // }}
                            />
                        ) : (
                            <video
                                src={mediaUrl}
                                controls
                                className={s.media}
                                style={{
                                    width: width || 488,
                                    height: height || 510,
                                }}
                            />
                        ))}
                    <div className={s.actions}>
                        <div className={s.actionAvatar}>
                            <AvatarComponent
                                avatarUrl={avatarUrl}
                                userName={userName}
                                width={26}
                                height={26}
                            />
                            <button>
                                <IconLinkedinLike /> Like
                            </button>
                        </div>
                        <button>
                            <IconIstagramComment /> Comment
                        </button>
                        <button>
                            <IconThreadsShare /> Share
                        </button>
                        <button>
                            <IconIstagramShare /> Send
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (platform === 'instagram') {
        return (
            <div className={`${s.container}`}>
                <div
                    className={s.instagram}
                    style={{
                        width: 400,
                    }}
                >
                    <div className={s.header}>
                        <div className={s.user}>
                            <AvatarComponent avatarUrl={avatarUrl} userName={userName} />
                            <span className={s.name}>{userName}</span>
                        </div>
                    </div>
                    {mediaUrl &&
                        (mediaType === 'image' ? (
                            <img
                                src={mediaUrl}
                                alt="media"
                                className={s.media}
                                // style={{
                                //     width: width || 488,
                                // }}
                            />
                        ) : (
                            <video
                                src={mediaUrl}
                                controls
                                className={s.media}
                                style={{
                                    width: width || 488,
                                    height: height || 610,
                                }}
                            />
                        ))}
                    <div className={s.actions}>
                        <div className={s.leftActions}>
                            <IconIstagramLike />
                            <IconIstagramComment />
                            <IconIstagramShare />
                        </div>
                        <div className={s.rightActions}>
                            <IconIstagramSave />
                        </div>
                    </div>
                    <div className={s.caption}>
                        <strong>{userName}</strong> {getShortText(text)}
                    </div>
                </div>
            </div>
        );
    }

    if (platform === 'threads') {
        return (
            <div className={`${s.container} `}>
                <div
                    className={s.threads}
                    style={{
                        width: width || 453,
                    }}
                >
                    <div className={s.post}>
                        <div className={s.avatarCol}>
                            <AvatarComponent avatarUrl={avatarUrl} userName={userName} />
                        </div>
                        <div className={s.contentCol}>
                            <div className={s.header}>
                                <span className={s.name}>{userName}</span>
                                {/* <span className={s.verified}>✔</span>  optional */}
                            </div>
                            <div className={s.text}>{getShortText(text)}</div>
                            {mediaUrl &&
                                (mediaType === 'image' ? (
                                    <img
                                        src={mediaUrl}
                                        alt="media"
                                        className={s.media}
                                        // style={{
                                        //     width: width || 453,
                                        //     height: height || 679,
                                        // }}
                                    />
                                ) : (
                                    <video
                                        src={mediaUrl}
                                        controls
                                        className={s.media}
                                        style={{
                                            width: width || 453,
                                            height: height || 679,
                                        }}
                                    />
                                ))}
                            <div className={s.actions}>
                                <IconIstagramLike />
                                <IconIstagramComment />
                                <IconThreadsShare />
                                <IconIstagramShare />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (platform === 'facebook') {
        return (
            <div className={`${s.container} `}>
                <div
                    className={s.facebook}
                    style={{
                        width: 400,
                    }}
                >
                    <div className={s.header}>
                        <AvatarComponent avatarUrl={avatarUrl} userName={userName} />
                        <div className={s.userInfo}>
                            <span className={s.name}>{userName}</span>
                        </div>
                        <div className={s.moreOptions}>⋯</div>
                    </div>

                    <div className={s.text}>{getShortText(text)}</div>

                    {mediaUrl &&
                        (mediaType === 'image' ? (
                            <img
                                src={mediaUrl}
                                alt="media"
                                className={s.media}
                                // style={{
                                //     width: width || 450,
                                //     height: height || 563,
                                // }}
                            />
                        ) : (
                            <video
                                src={mediaUrl}
                                controls
                                muted
                                className={s.media}
                                style={{
                                    width: width || 450,
                                    height: height || 563,
                                }}
                            />
                        ))}
                </div>
            </div>
        );
    }

    return <div>PreviewContainer</div>;
};

export default PreviewContainer;
